import React, { useEffect, useState } from "react";
import { LogOut, Inbox, FileText, Newspaper, Briefcase, ExternalLink } from "lucide-react";
import useSEO from "@/hooks/useSEO";
import Logo from "@/components/common/Logo";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useAuth } from "@/context/AuthContext";
import client from "@/lib/api";
import BlogManager from "@/components/admin/BlogManager";
import CaseManager from "@/components/admin/CaseManager";

function fmtDate(iso) {
  try { return new Date(iso).toLocaleString("es-CO", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
}

function SubmissionsTable({ endpoint, columns, testid }) {
  const [rows, setRows] = useState([]);
  useEffect(() => { client.get(endpoint).then(({ data }) => setRows(data)).catch(() => {}); }, [endpoint]);
  return (
    <div className="border border-white/10 overflow-x-auto" data-testid={testid}>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-white/10 bg-gema-surface">
            {columns.map((c) => <th key={c.key} className="text-left font-mono text-[10px] uppercase tracking-wider text-white/50 px-4 py-3">{c.label}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-b border-white/5 hover:bg-gema-surface/50">
              {columns.map((c) => (
                <td key={c.key} className="px-4 py-3 text-white/70 align-top max-w-xs">
                  {c.key === "created_at" ? fmtDate(r[c.key]) : (r[c.key] || "-")}
                </td>
              ))}
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={columns.length} className="px-4 py-6 text-white/40">Sin registros.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

export default function Dashboard() {
  const { user, logout } = useAuth();
  const [stats, setStats] = useState({ contacts: 0, quotes: 0, blog: 0, cases: 0 });
  useSEO({ title: "Panel de Administración", description: "Panel interno de GEMA." });

  useEffect(() => { client.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {}); }, []);

  const cards = [
    { label: "Cotizaciones", value: stats.quotes, icon: FileText },
    { label: "Mensajes", value: stats.contacts, icon: Inbox },
    { label: "Artículos", value: stats.blog, icon: Newspaper },
    { label: "Casos", value: stats.cases, icon: Briefcase },
  ];

  return (
    <div className="min-h-screen bg-gema-black">
      <header className="border-b border-white/10 bg-gema-ink sticky top-0 z-40">
        <div className="container-gema flex items-center justify-between h-20">
          <div className="flex items-center gap-6">
            <Logo />
            <span className="hidden sm:inline font-mono text-xs uppercase tracking-widest text-white/40">Panel Admin</span>
          </div>
          <div className="flex items-center gap-4">
            <a href="/" target="_blank" rel="noopener noreferrer" className="hidden sm:flex items-center gap-2 text-white/60 hover:text-white text-sm transition-colors"><ExternalLink className="h-4 w-4" /> Ver sitio</a>
            <span className="hidden md:inline text-white/50 text-sm">{user?.email}</span>
            <button onClick={logout} data-testid="logout-btn" className="flex items-center gap-2 text-white/70 hover:text-gema-red transition-colors text-sm"><LogOut className="h-4 w-4" /> Salir</button>
          </div>
        </div>
      </header>

      <main className="container-gema py-10">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
          {cards.map((c) => {
            const Icon = c.icon;
            return (
              <div key={c.label} className="border border-white/10 p-6 bg-gema-ink">
                <Icon className="h-6 w-6 text-gema-red" strokeWidth={1.5} />
                <p className="font-heading font-black text-3xl text-white mt-4">{c.value}</p>
                <p className="font-mono text-[10px] uppercase tracking-wider text-white/40 mt-1">{c.label}</p>
              </div>
            );
          })}
        </div>

        <Tabs defaultValue="quotes" className="w-full">
          <TabsList className="bg-gema-ink border border-white/10 rounded-none p-1 h-auto flex-wrap justify-start">
            <TabsTrigger value="quotes" data-testid="tab-quotes" className="rounded-none data-[state=active]:bg-gema-red data-[state=active]:text-white text-white/60 uppercase text-xs font-heading font-bold px-5 py-2.5">Cotizaciones</TabsTrigger>
            <TabsTrigger value="contacts" data-testid="tab-contacts" className="rounded-none data-[state=active]:bg-gema-red data-[state=active]:text-white text-white/60 uppercase text-xs font-heading font-bold px-5 py-2.5">Mensajes</TabsTrigger>
            <TabsTrigger value="blog" data-testid="tab-blog" className="rounded-none data-[state=active]:bg-gema-red data-[state=active]:text-white text-white/60 uppercase text-xs font-heading font-bold px-5 py-2.5">Blog</TabsTrigger>
            <TabsTrigger value="cases" data-testid="tab-cases" className="rounded-none data-[state=active]:bg-gema-red data-[state=active]:text-white text-white/60 uppercase text-xs font-heading font-bold px-5 py-2.5">Casos</TabsTrigger>
          </TabsList>

          <TabsContent value="quotes" className="mt-8">
            <SubmissionsTable
              endpoint="/admin/quotes"
              testid="quotes-table"
              columns={[
                { key: "created_at", label: "Fecha" },
                { key: "name", label: "Nombre" },
                { key: "email", label: "Correo" },
                { key: "phone", label: "Teléfono" },
                { key: "sector", label: "Sector" },
                { key: "service", label: "Servicio" },
                { key: "message", label: "Detalle" },
              ]}
            />
          </TabsContent>

          <TabsContent value="contacts" className="mt-8">
            <SubmissionsTable
              endpoint="/admin/contacts"
              testid="contacts-table"
              columns={[
                { key: "created_at", label: "Fecha" },
                { key: "name", label: "Nombre" },
                { key: "email", label: "Correo" },
                { key: "phone", label: "Teléfono" },
                { key: "subject", label: "Asunto" },
                { key: "message", label: "Mensaje" },
              ]}
            />
          </TabsContent>

          <TabsContent value="blog" className="mt-8"><BlogManager /></TabsContent>
          <TabsContent value="cases" className="mt-8"><CaseManager /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
